import {
  ClassGroupsTableProps,
  ClassListProps,
  GroupsProps
} from '@pages/admin/class-groups/types';
import {
  UserListProps,
  UserManagementTableProps
} from '@pages/admin/enrollment-users/types';
import { FAQFormData, FAQTableProps } from '@pages/admin/faq/types';
import { PreEnrolledTableProps } from '@pages/admin/pre-enrolled/types';
import envData from '../../../../config/env.json';
import {
  FilterOptionsProps,
  ResponseWithData,
  StandardResponse,
  buildQuery,
  del,
  get,
  post,
  put
} from './index';
const { apiLocation } = envData;
const base = apiLocation;

type FilterProps = {
  filter?: FilterOptionsProps;
};

type FilterDeleteUsers = {
  registrationId?: number;
  name?: string;
  email?: string;
  source?: string;
  createdAt?: string;
  term?: string;
  pageNumber?: number;
  pageSize?: number;
  search?: string;
  deletedAt?: string;
};

type DeleteUsersFilter = {
  filter: FilterDeleteUsers;
};

export interface DeleteUsersProps {
  id: string;
  access: string;
  createdAt: string;
  email: string;
  phone: string;
  cpf: string;
  registrationId: number;
  name: string;
  source: string;
  userId: string;
  term: number;
  group: string;
}

export interface DeleteResponseProps {
  result: DeleteUsersProps[];
  loading: boolean;
  dataCount: number;
}

const defaultOptions: RequestInit = {
  credentials: 'include'
};

export async function getUsers({
  filter
}: FilterProps): Promise<StandardResponse<UserManagementTableProps>> {
  const query: string = buildQuery(filter as FilterOptionsProps);

  const responseWithData: Promise<
    ResponseWithData<{ result: StandardResponse<UserManagementTableProps> }>
  > = get(`/user-management/all-users?${query}`);

  return responseWithData.then(res => res.data.result);
}

export async function getPreEnrolled({
  filter
}: FilterProps): Promise<StandardResponse<PreEnrolledTableProps>> {
  const query: string = buildQuery(filter as FilterOptionsProps);

  const responseWithData: Promise<
    ResponseWithData<{ result: StandardResponse<PreEnrolledTableProps> }>
  > = get(`/quiz-user/pre-enrolled?${query}`);

  return responseWithData.then(res => res.data.result);
}

export async function putEditUser(
  payload: UserListProps
): Promise<StandardResponse<UserListProps>> {
  const responseWithData: Promise<
    ResponseWithData<{ result: StandardResponse<UserListProps> }>
  > = put(`/user-management/edit-user`, payload);

  return responseWithData.then(res => res.data.result);
}

export async function getRequestedToDeleteUsers({ filter }: DeleteUsersFilter) {
  const query: string = buildQuery(filter);

  const responseWithData: Promise<ResponseWithData<DeleteResponseProps>> = get(
    `/users-requested-delete?${query}`
  );

  return responseWithData.then(res => res.data);
}

export async function getDeletedUsers({ filter }: DeleteUsersFilter) {
  const query: string = buildQuery(filter);

  const responseWithData: Promise<ResponseWithData<DeleteResponseProps>> = get(
    `/users-deleted?${query}`
  );

  return responseWithData.then(res => res.data);
}

export async function getAllClassGroups({
  filter
}: FilterProps): Promise<StandardResponse<ClassGroupsTableProps>> {
  const query: string = buildQuery(filter as FilterOptionsProps);

  const responseWithData: Promise<
    ResponseWithData<{ result: StandardResponse<ClassGroupsTableProps> }>
  > = get(`/class-groups/all-class-groups?${query}`);

  return responseWithData.then(res => res.data.result);
}

export async function postNewClassGroup(
  payload: ClassListProps
): Promise<StandardResponse<UserListProps>> {
  const responseWithData: Promise<
    ResponseWithData<{ result: StandardResponse<UserListProps> }>
  > = post(`/class-groups/new-class-group`, payload);

  return responseWithData.then(res => res.data.result);
}

export async function putEditClassGroup(
  payload: ClassListProps
): Promise<StandardResponse<UserListProps>> {
  const responseWithData: Promise<
    ResponseWithData<{ result: StandardResponse<UserListProps> }>
  > = put(`/class-groups/edit-class-group`, payload);

  return responseWithData.then(res => res.data.result);
}

export async function deleteClassGroup(
  payload: ClassListProps
): Promise<StandardResponse<ClassGroupsTableProps>> {
  const responseWithData: Promise<
    ResponseWithData<{ result: StandardResponse<ClassGroupsTableProps> }>
  > = del(`/class-groups/delete-class-group/${payload.id}`, payload);

  return responseWithData.then(res => res.data.result);
}

export async function putMoveUsersBetweenClassGroup(
  payload: ClassListProps
): Promise<StandardResponse<UserListProps>> {
  const responseWithData: Promise<
    ResponseWithData<{ result: StandardResponse<UserListProps> }>
  > = put(`/class-groups/move-users-class-group`, payload);

  return responseWithData.then(res => res.data.result);
}

export type WaitingList = {
  id: string;
  access: string;
  attempts: number;
  cpf: string;
  createdAt: string;
  email: string;
  phone: string;
  grade: number;
  group: string;
  name: string;
  position: number;
  hasAuditLog?: boolean;
  studentEvaluation: {
    generalKnowledge: {
      generalKnowledge: {
        easy: number;
        medium: number;
        difficult: number;
      };
      specificKnowledge: {
        easy: number;
        medium: number;
        difficult: number;
      };
      studentGrade: number;
    };
  };
  lastEvaluationDate: string;
};

export type WaitingListResponse = {
  waitingList: WaitingList[];
  dataCount: number;
  availableGroups: string[];
};

export type ReprovedAndDropoutList = {
  id: string;
  access: string;
  name: string;
  email: string;
  cpf: string;
  phone: string;
  grade: number;
  group: string;
  createdAt: string;
  attempts: number;
  status: string;
  hasAuditLog?: boolean;
};

export type ReprovedAndDropoutListResponse = {
  reprovedAndDropoutList: ReprovedAndDropoutList[];
  dataCount: number;
  availableGroups: string[];
  availableStatuses: string[];
};

export type FilterWaitingList = {
  position?: number;
  createdAt?: string;
  name?: string;
  email?: string;
  group?: string[];
  grade?: number;
  attempts?: number;
  sort?: string;
  order?: 'ASC' | 'DESC';
  pageNumber: number;
  pageSize?: number;
  search?: string;
};

export type FilterReprovedAndDropoutList = {
  createdAt?: string;
  name?: string;
  email?: string;
  group?: string[];
  grade?: number;
  attempts?: number;
  sort?: string;
  order?: 'ASC' | 'DESC';
  pageNumber: number;
  pageSize?: number;
  search?: string;
  status?: string[];
};

export type FilterConfirmedList = {
  createdAt?: string;
  pageNumber: number;
  pageSize?: number;
  search?: string;
  sort?: string;
  order?: string;
  group?: string[];
  source?: string[];
};

export type ConfirmedList = {
  id: string;
  access: string;
  attempts: number;
  cpf: string;
  createdAt: string;
  email: string;
  phone: string;
  grade: number;
  group: string;
  name: string;
  source: string;
  lastEvaluationDate: string;
  hasAuditLog?: boolean;
};

export type ConfirmedListResponse = {
  confirmedList: ConfirmedList[];
  dataCount: number;
  availableGroups: string[];
  availableSources: Array<{ value: string; label: string; groups: string[] }>;
};

export type FilterWaitingToConfirmList = {
  createdAt?: string;
  pageNumber: number;
  pageSize?: number;
  search?: string;
  sort: string;
  order?: string;
  group?: string[];
  source?: string[];
};

export type WaitingToConfirmList = {
  id: string;
  access: string;
  attempts: number;
  cpf: string;
  createdAt: string;
  email: string;
  phone: string;
  grade: number;
  group: string;
  name: string;
  source: string;
  lastEvaluationDate: string;
  hasAuditLog?: boolean;
};

export type WaitingToConfirmListResponse = {
  waitingToConfirmList: WaitingToConfirmList[];
  dataCount: number;
  availableGroups: string[];
  availableSources: Array<{ value: string; label: string; groups: string[] }>;
};

type WaitingListFilter = {
  filter: FilterWaitingList;
};

export async function getWaitingList(filter: WaitingListFilter) {
  const query: string = buildQuery(filter);
  const responseWithData: Promise<
    ResponseWithData<StandardResponse<WaitingListResponse>>
  > = get(`/admin/waiting-list?${query}`);

  return responseWithData.then(res => res.data);
}

export async function exportWaitingList(filter: WaitingListFilter) {
  const query: string = buildQuery(filter);
  const URL = `/admin/export-waiting-list?${query}`;
  const responseWithData = await fetch(`${base}${URL}`, {
    headers: {
      Accept:
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    },
    method: 'GET',
    credentials: 'include'
  });

  return responseWithData;
}

export async function getReprovedAndDropoutList(
  filter: FilterReprovedAndDropoutList
) {
  const query: string = buildQuery(filter);
  const responseWithData: Promise<
    ResponseWithData<StandardResponse<ReprovedAndDropoutListResponse>>
  > = get(`/admin/reproved-and-dropout-list?${query}`);

  return responseWithData.then(res => res.data);
}

export async function exportReprovedAndDropoutList(filter: WaitingListFilter) {
  const query: string = buildQuery(filter);
  const URL = `/admin/export-reproved-and-dropout-list?${query}`;
  const responseWithData = await fetch(`${base}${URL}`, {
    headers: {
      Accept:
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    },
    method: 'GET',
    credentials: 'include'
  });

  return responseWithData;
}

export async function getConfirmedList(filter: FilterReprovedAndDropoutList) {
  const query: string = buildQuery(filter);
  const responseWithData: Promise<
    ResponseWithData<StandardResponse<ReprovedAndDropoutListResponse>>
  > = get(`/admin/confirmed-list?${query}`);

  return responseWithData.then(res => res.data);
}

export async function exportConfirmedList(filter: FilterConfirmedList) {
  const query: string = buildQuery(filter);
  const URL = `/admin/export-confirmed-list?${query}`;
  const responseWithData = await fetch(`${base}${URL}`, {
    headers: {
      Accept:
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    },
    method: 'GET',
    credentials: 'include'
  });

  return responseWithData;
}

export async function getWaitingToConfirmList(
  filter: FilterReprovedAndDropoutList
) {
  const query: string = buildQuery(filter);
  const responseWithData: Promise<
    ResponseWithData<StandardResponse<ReprovedAndDropoutListResponse>>
  > = get(`/admin/waiting-to-confirm-list?${query}`);

  return responseWithData.then(res => res.data);
}

export async function exportWaitingToConfirmList(filter: FilterConfirmedList) {
  const query: string = buildQuery(filter);
  const URL = `/admin/export-waiting-to-confirm-list?${query}`;
  const responseWithData = await fetch(`${base}${URL}`, {
    headers: {
      Accept:
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    },
    method: 'GET',
    credentials: 'include'
  });

  return responseWithData;
}

export async function getGroups(): Promise<StandardResponse<GroupsProps>> {
  const responseWithData: Promise<
    ResponseWithData<{ result: StandardResponse<GroupsProps> }>
  > = get(`/states-national`);

  return responseWithData.then(res => res.data.result);
}

export type EmailDaylyMetrics = {
  date: string;
  stats: {
    metrics: {
      blocks: number;
      // eslint-disable-next-line @typescript-eslint/naming-convention
      bounce_drops: number;
      bounces: number;
      clicks: number;
      deferred: number;
      delivered: number;
      // eslint-disable-next-line @typescript-eslint/naming-convention
      invalid_emails: number;
      opens: number;
      processed: number;
      requests: number;
      // eslint-disable-next-line @typescript-eslint/naming-convention
      spam_report_drops: number;
      // eslint-disable-next-line @typescript-eslint/naming-convention
      spam_reports: number;
      // eslint-disable-next-line @typescript-eslint/naming-convention
      unique_clicks: number;
      // eslint-disable-next-line @typescript-eslint/naming-convention
      unique_opens: number;
      // eslint-disable-next-line @typescript-eslint/naming-convention
      unsubscribe_drops: number;
      unsubscribes: number;
    };
  }[];
};

interface EmailDaylyMetricsResponse {
  status: number;
  message: string;
  result: {
    data: EmailDaylyMetrics[];
  };
  success: boolean;
}

export async function getEmailDaylyMetrics(): Promise<EmailDaylyMetrics[]> {
  try {
    const { data } = await get<EmailDaylyMetricsResponse>(
      `/api/email-stats/all-daily-metrics`
    );
    return data.result.data;
  } catch (error) {
    console.error('Error fetching e-mail stats dashboard:', error);
    throw error;
  }
}

export interface EmailSentQuantity {
  today: number;
  week: number;
  month: number;
  emailSendingLimit: number;
  emailSendingLimitPercentage: number;
  lastUpdateTime: string;
}

interface EmailSentQuantityResponse {
  status: number;
  message: string;
  result: {
    data: EmailSentQuantity;
  };
}

export async function getEmailSentQuantity(): Promise<EmailSentQuantity> {
  try {
    const { data } = await get<EmailSentQuantityResponse>(
      `/api/email-stats/emails-sent-quantity`
    );
    return data.result.data;
  } catch (error) {
    console.error('Error fetching e-mail sent quantity:', error);
    throw error;
  }
}

type EmailStatsRecipient = {
  type: 'targetGroup' | 'fixedEmails' | 'regionalDepartment';
  targetGroup?: 'all' | 'class' | 'score' | 'class-and-score';
  class?: string;
  maxScore?: number;
  minScore?: number;
  fixedEmails?: string[];
  regionalDepartment?: string[];
  userStatus?: 'all' | 'active' | 'inactive' | 'evaded' | 'extended';
};

export async function getEmailStatsCounter(recipient: {
  payload: EmailStatsRecipient;
}): Promise<{ count: number }> {
  const { payload } = recipient;
  const { data } = await post<{
    result: {
      count: number;
    };
  }>('/api/email-stats/recipient-counter', { recipient: payload });
  console.log('response', data.result);
  return data.result;
}

export async function getNationalDepartmentMetrics(): Promise<void> {
  try {
    const response = await fetch(
      `${base}/api/admin/report/national-departament-metrics`,
      defaultOptions
    );
    const blob = await response.blob();

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'relatorio-metricas-nacionais.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error downloading national department metrics:', error);
    throw error;
  }
}

export async function getInactiveUsers(): Promise<void> {
  try {
    const [prResponse, piResponse] = await Promise.all([
      fetch(
        `${base}/api/admin/report/inactive-users?source=nacional_pr`,
        defaultOptions
      ),
      fetch(
        `${base}/api/admin/report/inactive-users?source=nacional_pi`,
        defaultOptions
      )
    ]);

    const prBlob = await prResponse.blob();
    const piBlob = await piResponse.blob();

    const downloadFile = (blob: Blob, filename: string) => {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    };

    downloadFile(prBlob, 'usuarios-inativos-pr.csv');
    downloadFile(piBlob, 'usuarios-inativos-pi.csv');
  } catch (error) {
    console.error('Error downloading inactive users:', error);
    throw error;
  }
}

export async function fetchUpdateEmailStats(): Promise<void> {
  try {
    const response = await post(`/api/email-stats/update-daily-metrics`, {});
    return response.data;
  } catch (error) {
    console.error('Error updating email stats:', error);
    throw error;
  }
}

// FAQ API functions
export async function getAllFAQs({
  filter
}: FilterProps): Promise<StandardResponse<FAQTableProps>> {
  const query: string = buildQuery(filter as FilterOptionsProps);

  const responseWithData: Promise<
    ResponseWithData<{ result: StandardResponse<FAQTableProps> }>
  > = get(`/faq/all-faqs?${query}`);

  return responseWithData.then(res => res.data.result);
}

export async function getPublicFAQs(filter?: {
  source?: string | null;
  group?: string | null;
}): Promise<StandardResponse<{ faqList: unknown[] }>> {
  const query: string = filter ? buildQuery(filter) : '';
  const responseWithData: Promise<
    ResponseWithData<{ result: StandardResponse<{ faqList: unknown[] }> }>
  > = get(`/faq/public${query ? `?${query}` : ''}`);

  return responseWithData.then(res => res.data.result);
}

export async function postCreateFAQ(
  payload: FAQFormData
): Promise<StandardResponse<FAQFormData>> {
  const responseWithData: Promise<
    ResponseWithData<{ result: StandardResponse<FAQFormData> }>
  > = post(`/faq/create`, payload);

  return responseWithData.then(res => res.data.result);
}

export async function putUpdateFAQ(
  payload: FAQFormData
): Promise<StandardResponse<FAQFormData>> {
  const responseWithData: Promise<
    ResponseWithData<{ result: StandardResponse<FAQFormData> }>
  > = put(`/faq/update`, payload);

  return responseWithData.then(res => res.data.result);
}

export async function deleteFAQ(payload: {
  id: string;
  registerEdit?: {
    changes: Record<string, unknown>;
    editorId: string;
    editorRegistrationId: number;
    editorEmail: string;
    editorName: string;
    action: string;
    timestamp: string;
  };
}): Promise<StandardResponse<{ id: string }>> {
  const responseWithData: Promise<
    ResponseWithData<{ result: StandardResponse<{ id: string }> }>
  > = del(`/faq/delete/${payload.id}`, payload);

  return responseWithData.then(res => res.data.result);
}

export async function putUpdateFAQOrder(payload: {
  items: { id: string; orderFaq: number }[];
}): Promise<StandardResponse<{ success: boolean }>> {
  const responseWithData: Promise<
    ResponseWithData<{ result: StandardResponse<{ success: boolean }> }>
  > = put(`/faq/update-order`, payload);

  return responseWithData.then(res => res.data.result);
}

// Class Group Users
export type ClassGroupUsersResponse = {
  totalUsers: number;
  pageNumber: number;
  pageSize: number;
  users: Array<{
    id: string;
    name: string;
    email: string;
    source: string;
    group: string;
    registrationId?: string;
    nationalDataComplement?: {
      state: string;
    };
    classGroupControl?: {
      acceptedInvite: boolean;
    };
  }>;
};

export async function getClassGroupUsers(
  classGroupId: string,
  params?: {
    pageNumber?: number;
    pageSize?: number;
    search?: string;
  }
): Promise<StandardResponse<ClassGroupUsersResponse>> {
  const query = buildQuery(params || {});
  const queryString = query ? `?${query}` : '';
  const url = `/class-groups/${classGroupId}/users${queryString}`;
  const responseWithData: Promise<
    ResponseWithData<{ result: StandardResponse<ClassGroupUsersResponse> }>
  > = get(url);

  return responseWithData.then(res => res.data.result);
}
// Enrollment Management API functions
export async function putEnrollStudent(payload: {
  studentId: string;
  classGroupId: string;
  justification: string;
}): Promise<StandardResponse<unknown>> {
  const responseWithData: Promise<
    ResponseWithData<{ result: StandardResponse<unknown> }>
  > = put(`/admin/enroll-student`, payload);

  return responseWithData.then(res => res.data.result);
}

export async function putMarkDropout(payload: {
  studentId: string;
  justification: string;
}): Promise<StandardResponse<unknown>> {
  const responseWithData: Promise<
    ResponseWithData<{ result: StandardResponse<unknown> }>
  > = put(`/admin/mark-dropout`, payload);

  return responseWithData.then(res => res.data.result);
}

export interface AuditLogRecord {
  id: string;
  actionType: 'admission' | 'dropout';
  studentId: string;
  classGroupId?: string;
  justification: string;
  adminId: string;
  adminName: string;
  timestamp: string;
  metadata?: {
    studentName?: string;
    studentEmail?: string;
    classGroupName?: string;
    classGroupIdentifier?: string;
    previousSource?: string;
  };
}

export type EnrollmentAuditLogsResponse = {
  success: boolean;
  result: AuditLogRecord[];
};

export async function getEnrollmentAuditLogs({
  studentId
}: {
  studentId: string;
}) {
  const responseWithData: Promise<
    ResponseWithData<StandardResponse<EnrollmentAuditLogsResponse>>
  > = get(`/admin/enrollment-audit-logs?studentId=${studentId}`);

  return responseWithData.then(res => res.data);
}

export async function getClassGroupsByGroup(
  group: string
): Promise<StandardResponse<ClassGroupsTableProps>> {
  const query: string = buildQuery({
    group,
    pageSize: 100
  });

  const responseWithData: Promise<
    ResponseWithData<{ result: StandardResponse<ClassGroupsTableProps> }>
  > = get(`/class-groups/all-class-groups?${query}`);

  return responseWithData.then(res => res.data.result);
}

// Email Logs API functions
export type EmailLogFilters = {
  page?: number;
  limit?: number;
  recipient?: string;
  subject?: string;
  status?: string;
  startDate?: string;
  endDate?: string;
};

export type EmailLogRecord = {
  recipient: string;
  subject: string;
  status: string;
  sendDate: string;
  sgMessageId: string;
  events: Array<{
    eventType: string;
    eventDate: string;
    sgEventId: string;
    timestamp: number;
  }>;
};

export type EmailLogsPaginatedResponse = {
  status: number;
  message: string;
  result: {
    data: EmailLogRecord[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  };
};

export async function getEmailLogs(
  filters: EmailLogFilters
): Promise<EmailLogsPaginatedResponse['result']> {
  try {
    const query: string = buildQuery(filters as unknown as FilterOptionsProps);
    const { data } = await get<EmailLogsPaginatedResponse>(
      `/api/email-logs?${query}`
    );
    return data.result;
  } catch (error) {
    console.error('Error fetching email logs:', error);
    throw error;
  }
}

export async function exportEmailLogs(
  filters: EmailLogFilters
): Promise<{ blob: Blob; limitApplied: boolean }> {
  const query: string = buildQuery(filters as unknown as FilterOptionsProps);
  const URL = `/api/email-logs/export?${query}`;
  const response = await fetch(`${base}${URL}`, {
    headers: {
      Accept:
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    },
    method: 'GET',
    credentials: 'include'
  });

  if (!response.ok) {
    throw new Error('Erro ao exportar logs de email');
  }

  const limitApplied = response.headers.get('X-Export-Limited') === 'true';
  const blob = await response.blob();

  return { blob, limitApplied };
}
